import { createAction } from 'redux-actions'

import { createApiActions } from '@tabeeb/shared/utils/actions'

import {
  SetFormAnswerStatusRequest,
  ContentFormListResponse,
  ContentFormRecordType,
  UserSignatureRequest,
  AttachFormRequest,
  CreateFormAnswerRequest,
  TriggerFormActionRequest,
  ContentFormStatusResponse,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import { ContentForm, ContentFormDetails, ContentFormRecord, Form, FormControlAnswer, FormFolder } from '../types'
import { DisplayAnswersMode, ExportFormsToCsvOptions } from '../enums'
import { FormAnswersFromFileLoadingStatus } from '../services/enums'

export const resetFormsState = createAction<void>('RESET_FORMS_STATE')

export const updateContentForms = createAction<ContentForm[]>('UPDATE_CONTENT_FORMS')
export const addContentForm = createAction<ContentFormDetails>('ADD_CONTENT_FORM')
export const removeContentForm = createAction<number>('REMOVE_CONTENT_FORM')
export const updateForm = createAction<ContentFormDetails>('UPDATE_FORM')
export const setFormAnswers = createAction<FormControlAnswer[]>('SET_FORM_ANSWERS')
export const updateFormAnswers = createAction<FormControlAnswer>('UPDATE_FORM_ANSWERS')
export const clearFormAnswers = createAction<FormControlAnswer>('CLEAR_FORM_ANSWERS')
export const setSelectedTabs = createAction<number[]>('SET_SELECTED_TABS')
export const setReadonlySelectedTabs = createAction<number[]>('SET_READONLY_SELECTED_TABS')
export const setSelectedUserId = createAction<number>('SET_SELECTED_USER_ID')
export const setCurrentForm = createAction('SET_CURRENT_FORM')
export const unsetCurrentForm = createAction<void>('UNSET_CURRENT_FORM')
export const updateReattachCount = createAction<void>('UPDATE_REATTACH_COUNT')
export const updateContentForm = createAction<ContentForm>('UPDATE_CONTENT_FORM')

export const getContentForms = createApiActions<{ contentId: number }, ContentForm[]>('GET_CONTENT_FORMS')

export const getAvailableForms = createApiActions<{ allForms?: boolean }, Form[]>('GET_AVAILABLE_FORMS')

export const startLoadingFormAnswers = createAction<void>('START_LOADING_FORM_ANSWERS')
export const getFormControls = createApiActions<{ contentFormId: number }, ContentFormDetails>('GET_FORM_CONTROLS')

export const setCurrentTabs = createAction<number[]>('SET_CURRENT_TABS')

export const setIsAnswerInProgress = createAction<boolean>('SET_IS_ANSWER_IN_PROGRESS')
export const sendFormAnswer = createApiActions<CreateFormAnswerRequest, FormControlAnswer>('SEND_FORM_ANSWER')

export const getFormAnswers = createApiActions<
  { contentFormId: number; userId: number; pageId?: number | null },
  FormControlAnswer[]
>('GET_FORM_ANSWERS')

export const getTabsAnswers = createApiActions<
  {
    currentFormId: number
    selectedUserId?: number | null
    formTabs: string
    pageId?: number | null
  },
  FormControlAnswer[]
>('GET_TABS_ANSWERS')

export const attachForm = createApiActions<AttachFormRequest, void>('ATTACH_FORM')
export const detachForm = createApiActions<{ contentId: number; formId: number }, void>('DETACH_FORM')

export const detachCurrentForm = createAction<void>('DETACH_CURRENT_FORM')

export const exportFormToCsv = createAction<{ option?: ExportFormsToCsvOptions }>('EXPORT_FORM_TO_CSV')
export const apiExportFormToCsv = createApiActions<
  { contentId: number; userId: number; pageId?: number | null; formsIds: number[] | null },
  string
>('EXPORT_FORM_TO_CSV')

export const exportFormToCustomCsv = createAction<void>('EXPORT_FORM_TO_CUSTOM_CSV')
export const apiExportFormToCustomCsv = createApiActions<{ contentId: number; contentFormId: number }, unknown>(
  'EXPORT_FORM_TO_CUSTOM_CSV'
)

export const exportFormToPdf = createAction<void>('EXPORT_FORM_TO_PDF')
export const apiExportFormToPdf = createApiActions<{
  userId?: number
  contentFormId: number
  formTabs?: number[]
  pageId?: number | null
}>('EXPORT_FORM_TO_PDF')

export const clearFormAnswer = createApiActions<number, void>('CLEAR_FORM_ANSWER')

export const updateAnswerStatus = createAction<FormControlAnswer>('UPDATE_ANSWER_STATUS')

export const apiUpdateAnswerStatus = createApiActions<SetFormAnswerStatusRequest, void>('UPDATE_ANSWER_STATUS')

export const getAnswersStatusesCount = createApiActions<{ contentFormId: number }, ContentFormListResponse>(
  'GET_ANSWERS_STATUSES_COUNT'
)

export const setAnswersStatusesCount = createAction<ContentFormStatusResponse[]>('SET_ANSWERS_STATUSES_COUNT')

export const proceedToControlAnswerPage = createAction<FormControlAnswer>('PROCEED_TO_CONTROL_ANSWER_PAGE')

export const getContentForm = createApiActions<
  { ContentId: number; FormId: number; UserId: number },
  ContentFormDetails
>('GET_CONTENT_FORM')

export const setDisplayAnswersMode = createAction<DisplayAnswersMode>('SET_DISPLAY_ANSWERS_MODE')

export const getLatestFormAnswers = createAction<void>('GET_LATEST_FORM_ANSWERS')
export const apiGetLatestFormAnswers = createApiActions<
  { contentFormId: number; contentSharingId?: number },
  FormControlAnswer[]
>('GET_LATEST_FORM_ANSWERS')

export const getLatestTabsAnswers = createApiActions<{
  contentFormId: number
  formTabs: string
  contentSharingId?: number
}>('GET_LATEST_TABS_ANSWERS')

export const getAllMyLatestAnswers = createAction<void>('GET_ALL_MY_LATEST__ANSWERS')

export const openSwitchFormDialog = createAction<void>('OPEN_SWITCH_FORM_DIALOG')
export const closeSwitchFormDialog = createAction<void>('CLOSE_SWITCH_FORM_DIALOG')

export const setLastAttachedForm = createAction<{
  formId: number
  isAttachedFromCondition: boolean
}>('SET_LAST_ATTACHED_FORM')

export const setFormAnswersFromFileLoadingState = createAction<{
  status: FormAnswersFromFileLoadingStatus
  errors?: string[]
}>('SET_FORM_ANSWERS_FROM_FILE_LOADING_STATE')

export const openInfoMessageDialog = createAction<{ id: string; message: string }>('OPEN_INFO_MESSAGE_DIALOG')
export const closeInfoMessageDialog = createAction<string>('CLOSE_INFO_MESSAGE_DIALOG')

export const checkLastAttachedForm = createAction<void>('CHECK_LAST_ATTACHED_FORM')

export const triggerFormAction = createApiActions<TriggerFormActionRequest, void>('TRIGGER_FORM_ACTION')

export const openForm = createAction<number>('OPEN_FORM')
export const openReadonlyForm = createAction<number>('OPEN_READONLY_FORM')
export const setInitialValues = createAction<void>('SET_INITIAL_VALUES')

// TODO: update to enum
export const setAnswerStatusFilter = createAction<string[]>('SET_ANSWER_STATUS_FILTER')

export const setSelectedRecord = createAction<number>('SET_SELECTED_RECORD')

export const saveFormRecord = createApiActions<
  { contentFormId: number; userId: number; type: ContentFormRecordType },
  FormControlAnswer
>('SAVE_FORM_RECORD')

export const addFormRecord = createAction<ContentFormRecord>('ADD_FORM_RECORD')

export const removeFormRecord = createAction<number>('REMOVE_FORM_RECORD')

export const apiRemoveFormRecord = createApiActions<number, void>('REMOVE_FORM_RECORD')

export const getFormAnswersByRecord = createApiActions<{ recordId: number; formTabs?: string }, FormControlAnswer[]>(
  'GET_FORM_ANSWERS_BY_RECORD'
)

export const setFormAnswersLoading = createAction<boolean>('SET_FORM_ANSWERS_LOADING')
export const getCounterInfo = createAction<void>('GET_COUNTER_INFO')

export const updateReviewerAnswerStatus = createApiActions<SetFormAnswerStatusRequest, void>(
  'UPDATE_REVIEWER_ANSWER_STATUS'
)

export const setFormAnswerStatus = createAction<SetFormAnswerStatusRequest>('SET_FORM_ANSWER_STATUS')

export const getAvailableFormsByFolderId = createApiActions<number, ContentForm[]>('GET_AVAILABLE_FORMS_BY_FOLDER_ID')

export const getAllFoldersInForms = createApiActions<void, FormFolder[]>('GET_ALL_FOLDERS_IN_FORMS')

export const openFolder = createAction<FormFolder | null>('OPEN_FOLDER_IN_FORMS')
export const setCurrentFolder = createAction<FormFolder>('SET_CURRENT_FOLDER_IN_FORMS')
export const clearCurrentFolder = createAction<void>('CLEAR_CURRENT_FOLDER_IN_FORMS')

export const getDeletedUserIdsWithAnsweredQuestions = createApiActions<{ contentId: number }, number[]>(
  'GET_DELETED_USER_IDS_WITH_ANSWERED_QUESTIONS'
)

export const createUserSignature = createAction<UserSignatureRequest>('CREATE_USER_SIGNATURE')
